import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import React from "react";
import Link from "next/link";
import Head from "next/head";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import * as CmsHttp from "@app/http/cms";
import { sliceMetaDescription } from "@app/utilities/helpers";
import { COMPANY_PHONE_NUMBER, META_DESCRIPTION, META_TITLE, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import { Service } from "@models/Service";
import HorizontalLayoutViewComp from "@components/hocs/HorizontalLayoutView/HorizontalLayoutViewComp";
import { FaqsList } from "@pages/faqs";
import CmsAbstract from "@components/common/CmsAbstract";
import { format } from "date-fns";
import { CmsMeta } from "@app/http/cms/components/CmsMeta";
import { withUpdateCmsVisit } from "@app/hooks/WithUpdateCmsVisit";
import { CustomUri } from "@models/CustomUri";
import CustomUriPage from "@pages/[...paths]";

interface IServiceDetailPage {
  slug: string;
  type: string;
  service: Service;
  uri: string;
  customUri: CustomUri;
}

const ServiceDetailPage: NextPage<IServiceDetailPage> = (props: IServiceDetailPage) => {
  if (props.customUri) {
    return <CustomUriPage uri={props.uri} customUri={props.customUri} />;
  }

  const { service } = props,
    title = [service.type, "Services"].join(" "),
    serviceMetaDescription = sliceMetaDescription(service.content.text),
    faqs = service?.faqs || [],
    blogs = service?.blogs || [],
    showPhone = ["marriage-retreat", "neurofeedback"].indexOf(service.slug) >= 0;

  /**
   * Meta contents
   */
  let metaTitle = META_TITLE || "",
    metaDescription = META_DESCRIPTION || "",
    metaUrl = META_URL || "",
    metaOgImg = META_URL + `/logo.png`,
    metaTwitterImg = META_URL + `/logo.png`;

  if (service) {
    metaTitle = `${service.name} | Heritage Counseling`;
    metaDescription = serviceMetaDescription || metaDescription;
    metaUrl = `${META_URL}/services/${service.typeSlug}/${service.slug}`;
    metaOgImg = service.image?.url || metaOgImg;
    metaTwitterImg = service.image?.url || metaTwitterImg;
  }

  // Override metas using pageMeta
  const pageMeta = (service?.meta || {}) as CmsMeta;
  if (Object.keys(pageMeta).length > 0) {
    metaTitle = pageMeta?.title || metaTitle;
    metaDescription = pageMeta?.description || metaDescription;
    metaOgImg = pageMeta?.ogImage?.url || metaOgImg;
    metaTwitterImg = pageMeta?.twitterImage?.url || metaTwitterImg;
  }

  return (
    <PageLayoutV1 title={title} fluidContainer={true}>
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaOgImg} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaTwitterImg} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
      </Head>
      <div className="service-detail-page" data-testid="service-detail-page">
        <div className="container">
          <nav className="mb-5 mb-lg-10" role="navigation">
            <Link href="/services">
              <a className="link-back">
                <i className="fa fa-angle-left"></i>
                Back to Services
              </a>
            </Link>
          </nav>
        </div>
        {!!service?.secondaryContent?.html && (
          <div className="service-wrapper">
            <div className="container">
              <div className="cms-content" dangerouslySetInnerHTML={{ __html: service.secondaryContent.html }}></div>
            </div>
          </div>
        )}
        {service ? (
          <div className="service-wrapper">
            <div className="container">
              <HorizontalLayoutViewComp
                imageProps={{
                  src: service.image.src,
                  alt: service.name,
                  width: service.image.width,
                  height: service.image.height,
                }}
                header={
                  <div className="header">
                    <h2 className="title">{service.name || ""}</h2>
                  </div>
                }
              >
                <div className="cms-content" dangerouslySetInnerHTML={{ __html: service.content.html }}></div>
                {showPhone && (
                  <a title="Phone" aria-label="Phone" href={`tel:${COMPANY_PHONE_NUMBER}`} className="btn btn-external btn-main px-5 py-3 mt-5" rel="noopener noreferrer">
                    <span>Call for more information</span>
                  </a>
                )}
              </HorizontalLayoutViewComp>
            </div>
          </div>
        ) : (
          <p className="text-center">No service found</p>
        )}
        {!!service.abstract && (
          <section className="rel-section abstract-section">
            <div className="container">
              <CmsAbstract className="abstract-wrapper" data={service.abstract} />
            </div>
          </section>
        )}
        {!!faqs?.length && (
          <section className="rel-section service-faqs-section">
            <div className="container">
              <h3 className="rel-title">FAQ About {service.name} </h3>
              <FaqsList ssrFaqs={faqs} />
            </div>
          </section>
        )}
        {!!blogs?.length && (
          <section className="rel-section service-blogs-section">
            <div className="container">
              <h3 className="rel-title">Related Blogs</h3>
              <div className="blogs-list">
                {blogs.map((blog, idx) => {
                  const bodyText = sliceMetaDescription(blog.content.text, 210),
                    publishedDateStr = format(blog.createdAt, "PP");

                  return (
                    <Link key={idx} href={`/blogs/${blog.slug}`}>
                      <a className="card blog-card">
                        <div className="row">
                          <div className="col-md-4 col-xl-3 img-container">
                            <img className="img-blog" src={blog.featuredImage?.src || ""} alt={blog.title} height={150} width={150} />
                          </div>
                          <div className="col-md-8 col-xl-9">
                            <div className="card-body">
                              <h5 className="card-title">{blog.title}</h5>
                              <p className="card-text">{bodyText}</p>
                              <p className="card-text">
                                <small className="text-muted">Published on {publishedDateStr}</small>
                              </p>
                            </div>
                          </div>
                        </div>
                      </a>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
        <div className="container">
          <nav className="mt-5 mt-lg-10" role="navigation">
            <Link href="/services">
              <a className="link-back">
                <i className="fa fa-angle-left"></i>
                Back to Services
              </a>
            </Link>
          </nav>
        </div>
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = (params && Array.isArray(params?.slug) ? params.slug[0] : params?.slug) as string,
    type = (params && Array.isArray(params?.type) ? params.type[0] : params?.type) as string,
    uri = `/services/${type}/${slug}`,
    customUri = await CmsHttp.getCustomUriByUri(uri),
    hasCustomUri = !!customUri.uri,
    service = hasCustomUri ? null : await CmsHttp.getServiceBySlug(slug);

  return {
    props: {
      type,
      slug,
      service: service || null,
      uri,
      customUri: hasCustomUri ? customUri : null,
    } as IServiceDetailPage,
    notFound: !service && !hasCustomUri,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const models = await CmsHttp.getServices(),
    paths = models
      .map((model) => {
        return {
          slug: model?.slug || "",
          type: model?.typeSlug,
        };
      })
      .filter((path) => {
        return !!path.slug;
      });

  return {
    paths: paths.map((path) => {
      return {
        params: path,
      };
    }),
    fallback: "blocking",
  };
};

export default withUpdateCmsVisit(ServiceDetailPage);
